/** @format */

import {
  CompareState,
  DisplayableProductListData,
  FilterUpdatePayload,
  PersonalizedPinnedContent,
  PinnedContent,
  ProductList,
  ProductListCore,
  PzPlpBannerItem,
} from '@plp/data-access/interfaces';
import {
  CriteoViewProductsResponse,
  CustomBadge,
  PlpPromoMessages,
  ProductAvailabilityCollection,
  PromoDetailsMap,
} from '@shared/data-access/interfaces';
import { createAction, props } from '@ngrx/store';

export const plpResolverOnLoadDataInitialized = createAction(
  '[PLP Resolver] On Load Data Initialized',
  props<{
    data: Pick<
      ProductListCore,
      | 'displayableProductListData'
      | 'pageNumber'
      | 'pageSize'
      | 'productListSEO'
      | 'selectedFilters'
      | 'selectedSort'
    >;
  }>(),
);

export const availabilityFilterChanged = createAction(
  '[Shipping Options] Availability Filter Changed',
  props<FilterUpdatePayload>(),
);

export const plpResolverSEONoCategories = createAction(
  '[PLP Resolver] SEO API Response Has No Categories',
);

export const plpResolverSEOError = createAction('[PLP Resolver] SEO API Call Failed');

export const plpResolverAPIDataInitialized = createAction(
  '[PLP Resolver] API Data Initialized',
  props<{
    data: Pick<ProductListCore, 'productList'> & Pick<CompareState, 'compareBarProducts'>;
  }>(),
);

export const plpResolverProductListFetchErrored = createAction(
  '[PLP Resolver] Product List Fetch Errored',
  props<{
    fallbackProductList: ProductList;
  }>(),
);

export const secondPassProductListFetched = createAction(
  '[PLP Resolver] Second Pass Product List Fetched',
  props<{ productList: ProductList }>(),
);

export const srlpResolverOnLoadDataInitialized = createAction(
  '[SRLP Resolver] On Load Data Initialized',
  props<{
    data: Pick<ProductListCore, 'pageNumber' | 'pageSize' | 'selectedFilters' | 'selectedSort'>;
  }>(),
);

export const srlpResolverAPIDataInitialized = createAction(
  '[SRLP Resolver] API Data Initialized',
  props<{
    data: Pick<ProductListCore, 'productList'> & Pick<CompareState, 'compareBarProducts'>;
  }>(),
);

export const srlpResolverProductListFetchNoData = createAction(
  '[SRLP Resolver] Product List Fetch Has No Data',
);

export const srlpResolverProductListFetchErrored = createAction(
  '[SRLP Resolver] Product List Fetch Errored',
);

export const criteoProductsResolvedEmpty = createAction(
  '[Product List Effects] Criteo Products Resolved to Empty',
);

export const criteoProductsFetchSuccessful = createAction(
  '[Product List Effects] Criteo Products Fetch Successful',
  props<{ criteoProducts: CriteoViewProductsResponse }>(),
);

export const criteoProductsFetchErrored = createAction(
  '[Product List Effects] Criteo Products Fetch Errored',
);

export const filteringPinnedContentClicked = createAction(
  '[Pinned Content] Filtering Pinned Content Clicked',
  props<FilterUpdatePayload>(),
);

export const pinnedContentResolvedEmpty = createAction(
  '[Product List Effects] Pinned Content Resolved to Empty',
);

export const pinnedContentFetchSuccessful = createAction(
  '[Product List Effects] Pinned Content Fetch Successful',
  props<{ pinnedContent: PinnedContent | PersonalizedPinnedContent }>(),
);

export const pinnedContentFetchErrored = createAction(
  '[Product List Effects] Pinned Content Fetch Errored',
);

// Initializers
export const setInitialPageNumber = createAction(
  '[Product List] Set Initial Page Number',
  props<{ pageNumber: number }>(),
);

export const setInitialPageSize = createAction(
  '[Product List] Set Initial Page Size',
  props<{ pageSize: number }>(),
);

export const productListLoadSuccessful = createAction(
  '[Product List Effects] Product List Load Successful',
  props<{
    productList: ProductList;
  }>(),
);

export const productListLoadErrored = createAction(
  '[Product List Effects] Product List Load Errored',
);

// Setters
export const productAvailabilityFetched = createAction(
  '[Product List Effects] Product Availability Fetched',
  props<{ productAvailability: ProductAvailabilityCollection | null | [] }>(),
);

export const productListPromoFetched = createAction(
  '[Product List Effects] Product List Promo Fetched',
  props<{ productPromotion: PromoDetailsMap | PlpPromoMessages | null }>(),
);

export const sortSelectionChanged = createAction(
  '[Sort] Sort Selection Changed',
  props<{ selectedSort: { label: string; value: number } }>(),
);

export const pageNumberChanged = createAction(
  '[Bottom Pagination] Page Number Changed',
  props<{ pageNumber: number }>(),
);

export const pageSizeChanged = createAction(
  '[Top Pagination] Page Size Changed',
  props<{ pageSize: number }>(),
);

export const setIsProductListPageValid = createAction(
  '[Product List] Set whether current page is a valid PLP',
  props<{ isProductListPageValid: boolean }>(),
);

export const displayableProductListBuilt = createAction(
  '[Product List Effects] Displayable Product List Built',
  props<{ displayableProductListData: DisplayableProductListData }>(),
);

export const filterChipsClearAllClicked = createAction('[Filter Chips] Clear All Clicked');

export const filterContainerClearAllClicked = createAction('[Filter Container] Clear All Clicked');

export const selectedFiltersUpdated = createAction(
  '[Filter] Selected Filters Updated',
  props<FilterUpdatePayload>(),
);

export const selectedFilterRemoved = createAction(
  '[Filter Chips] Selected Filter Chip Removed',
  props<{ filterType: string; filterLabel: string }>(),
);

export const searchInsteadClicked = createAction(
  '[Product List Header] Search Instead Clicked',
  props<{ searchTerm: string }>(),
);

export const snbAudienceChanged = createAction(
  '[Product List API] SNB Audience Changed',
  props<{ snbAudience: string }>(),
);

export const productListLoadTriggered = createAction(
  '[Product List Effects] Product List Load Triggered',
  (props: { searchTerm?: string } = {}) => props,
);

export const appInitializerFeatureFlagsSet = createAction(
  '[Product List] Set Feature Flags in App Initializer',
  props<{ featureFlags: Record<string, boolean> }>(),
);

export const featureConfigCustomBadgesSet = createAction(
  '[Feature Config] Custom Badges Set',
  props<{
    customBadges: Record<string, CustomBadge>;
  }>(),
);

export const pzPlpBannerFetchSuccessful = createAction(
  '[Product List Effects] PZ PLP Banner Fetch Successful',
  props<{ pzPlpBanners: PzPlpBannerItem[] }>(),
);

export const pzPlpBannerFetchErrored = createAction(
  '[Product List Effects] PZ PLP Banner Fetch Errored',
);

export const pzPlpBannerResolvedEmpty = createAction(
  '[Product List Effects] PZ PLP Banner Resolved Empty',
);
